<template lang="pug">
SmxForm(v-loading="loading")
  SmxFormItem(v-if="props.selectTemplate", :label="$t('ticketType')", required)
    el-select(v-model="selectedTemplateId", clearable)
      el-option(
        v-for="ticketTemplate in ticketTemplates",
        :key="ticketTemplate.uuid",
        :label="`${ticketTemplate.name} (${ticketTemplate.type})`",
        :value="ticketTemplate.uuid"
      )

  template(v-if="template")
    slot(name="before-title", :values="propertyValues", :template="template")

    template(v-for="item in formItems", :key="item.key")
      template(v-if="item.kind === 'title'")
        SmxFormItem(:label="$t('title')", required)
          el-input(v-model="name")
        slot(
          name="after-title",
          :values="propertyValues",
          :template="template"
        )

      template(v-else)
        SmxFormItem(
          v-if="isPropertyVisible(item.property)",
          :label="props.propertyLabels[item.property.customId ?? ''] ?? item.property.name",
          :description="item.property.description",
          :required="item.property.constraint?.settings?.mandatory"
        )
          slot(
            v-if="hasPropertySlot(item.property)",
            :name="`property-${item.property.customId}`",
            :property="item.property",
            :model-value="propertyValues[item.property.customId ?? '']",
            :disabled="isPropertyDisabled(item.property)",
            :update:model-value="getPropertyValueUpdater(item.property.customId)",
            :update-values="updatePropertyValues",
            :values="propertyValues"
          )
          TwinPropertyInput(
            v-else,
            :prop="item.property",
            :model-value="propertyValues[item.property.customId ?? '']",
            :disabled="isPropertyDisabled(item.property)",
            @update:model-value="updatePropertyValue(item.property.customId, $event)"
          )
        slot(
          v-if="isPropertyVisible(item.property)",
          name="after-property",
          :property="item.property",
          :values="propertyValues",
          :template="template"
        )

    slot(
      name="after-properties",
      :values="propertyValues",
      :template="template"
    )

  template(#actions)
    el-button(
      v-if="props.showDraft",
      :disabled="!template || creating",
      @click="createDraft"
    ) {{ $t("createDraft") }}
    el-button(
      type="primary",
      icon="Plus",
      :loading="creating",
      :disabled="!template || creating",
      @click="createTicket"
    ) {{ $t("create") }}
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useSlots, watch } from "vue";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import SmxFormItem from "@shared/components/elements/SmxFormItem.vue";
import TwinPropertyInput from "@shared/components/twinManagement/components/TwinPropertyInput.vue";
import { ticketProps } from "@shared/components/tickets/constants";
import {
  createTwinWithProperties,
  type TwinProperty,
  type TwinResponse,
} from "@shared/services/twinManagement";
import { useAuthStore } from "@shared/stores/auth";
import { useTwinStore } from "@shared/stores/twinManagement";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import { applicationLanguage } from "@shared/util/languages";
import { useMachineRestrictionStore } from "@shared/maintenance/machineRestrictions/store";
import {
  searchMachineRestrictions,
  type MachineRestrictionResponse,
} from "@shared/services/settings";

export interface TicketCreatePropertyRestriction {
  hidden?: boolean;
  locked?: boolean;
}

const props = withDefaults(
  defineProps<{
    templateType?: string;
    // eslint-disable-next-line no-unused-vars
    templateFilter?: (template: TwinResponse) => boolean;
    selectTemplate?: boolean;
    titleOrder?: number;
    propertyValues?: Record<string, unknown>;
    defaultPropertyValues?: Record<string, unknown>;
    propertyRestrictions?: Record<string, TicketCreatePropertyRestriction>;
    propertyVisibility?: Record<string, boolean>;
    includedPropertyCustomIds?: string[];
    suppressedPropertyCustomIds?: string[];
    propertyLabels?: Record<string, string>;
    additionalProperties?: Record<string, unknown>;
    additionalRequest?: Record<string, unknown>;
    additionalMissingRequiredFields?: () => string[];
    showDraft?: boolean;
    createStatus?: string;
    draftStatus?: string;
  }>(),
  {
    templateType: undefined,
    templateFilter: undefined,
    selectTemplate: false,
    titleOrder: Number.NEGATIVE_INFINITY,
    propertyValues: () => ({}),
    defaultPropertyValues: () => ({ [ticketProps.priority]: 0 }),
    propertyRestrictions: () => ({}),
    propertyVisibility: () => ({}),
    includedPropertyCustomIds: () => [],
    suppressedPropertyCustomIds: () => [],
    propertyLabels: () => ({}),
    additionalProperties: () => ({}),
    additionalRequest: () => ({}),
    additionalMissingRequiredFields: () => [],
    showDraft: false,
    createStatus: "new",
    draftStatus: "draft",
  },
);

const emit = defineEmits<{
  "update:propertyValues": [values: Record<string, unknown>];
  created: [ticketId: string];
}>();

const { t } = useI18n();
const route = useRoute();
const authStore = useAuthStore();
const twinStore = useTwinStore();
const machineRestrictionStore = useMachineRestrictionStore();
const slots = useSlots();

const name = defineModel<string>("name", { default: "" });
const description = defineModel<string>("description", { default: "" });
const selectedTemplateId = ref<string | null>(null);
const creating = ref(false);
const queryMachineRestriction = ref<MachineRestrictionResponse | null>(null);
const queryMachineRestrictionLoading = ref(false);

const templateLoadingState = computed(() => twinStore.templatesLoadingState);
const loading = computed(
  () =>
    templateLoadingState.value.loading ||
    machineRestrictionStore.loading ||
    queryMachineRestrictionLoading.value,
);
const ticketTemplates = computed(() =>
  (templateLoadingState.value.result ?? []).filter(
    (item) =>
      item.active &&
      (!props.templateType || item.type === props.templateType) &&
      (!props.templateFilter || props.templateFilter(item)),
  ),
);

watch(
  ticketTemplates,
  (templates) => {
    if (
      props.selectTemplate &&
      templates.length > 0 &&
      selectedTemplateId.value === null
    ) {
      selectedTemplateId.value = templates[0]!.uuid;
    }
  },
  { immediate: true },
);

const template = computed(() => {
  if (props.selectTemplate) {
    return ticketTemplates.value.find(
      (item) => item.uuid === selectedTemplateId.value,
    );
  }
  return ticketTemplates.value[0];
});

const machineRestriction = computed(() => {
  const installedMachineRestriction = machineRestrictionStore.result?.find(
    (item) =>
      item.templateId === template.value?.uuid ||
      item.templateId === template.value?.type,
  );
  return installedMachineRestriction ?? queryMachineRestriction.value;
});
const machinePropertyRestrictions = computed<
  Record<string, TicketCreatePropertyRestriction>
>(() =>
  Object.fromEntries(
    (machineRestriction.value?.propertyDefaults ?? []).map((restriction) => [
      restriction.customId,
      { hidden: restriction.hidden, locked: restriction.locked },
    ]),
  ),
);
const propertyRestrictions = computed<
  Record<string, TicketCreatePropertyRestriction>
>(() => {
  const customIds = new Set([
    ...Object.keys(props.propertyRestrictions),
    ...Object.keys(machinePropertyRestrictions.value),
  ]);
  return Object.fromEntries(
    [...customIds].map((customId) => [
      customId,
      {
        hidden:
          props.propertyRestrictions[customId]?.hidden ||
          machinePropertyRestrictions.value[customId]?.hidden,
        locked:
          props.propertyRestrictions[customId]?.locked ||
          machinePropertyRestrictions.value[customId]?.locked,
      },
    ]),
  );
});

const createFormProperties = computed(() =>
  (template.value?.properties ?? [])
    .filter(
      (property) =>
        (property.isUserRelevant &&
          property.customId !== ticketProps.status &&
          property.constraint.settings?.editableOnCreate) ||
        (property.customId !== undefined &&
          props.includedPropertyCustomIds.includes(property.customId)),
    )
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0)),
);

type FormItem =
  | { key: "title"; kind: "title"; order: number }
  | { key: string; kind: "property"; order: number; property: TwinProperty };

const formItems = computed<FormItem[]>(() =>
  [
    { key: "title" as const, kind: "title" as const, order: props.titleOrder },
    ...createFormProperties.value.map((property) => ({
      key: property.uuid,
      kind: "property" as const,
      order: property.order ?? 0,
      property,
    })),
  ].sort((left, right) => left.order - right.order),
);

const propertyValues = computed(() => props.propertyValues);

function updatePropertyValue(customId: string | undefined, value: unknown) {
  if (!customId) return;
  updatePropertyValues({
    ...props.propertyValues,
    [customId]: value,
  });
}

function getPropertyValueUpdater(customId: string | undefined) {
  return (value: unknown) => updatePropertyValue(customId, value);
}

function updatePropertyValues(values: Record<string, unknown>) {
  emit("update:propertyValues", {
    ...props.propertyValues,
    ...values,
  });
}

function hasPropertySlot(property: TwinProperty) {
  return Boolean(property.customId && slots[`property-${property.customId}`]);
}

function isPropertyVisible(property: TwinProperty) {
  if (!property.customId) return false;
  return (
    props.propertyVisibility[property.customId] !== false &&
    !props.suppressedPropertyCustomIds.includes(property.customId) &&
    !propertyRestrictions.value[property.customId]?.hidden
  );
}

function isPropertyDisabled(property: TwinProperty) {
  if (!property.customId) return true;
  return Boolean(propertyRestrictions.value[property.customId]?.locked);
}

function getPropertyValueForSaving(property: TwinProperty, value: unknown) {
  if (property.type !== "twinRelation" || value === null) return value;

  const relationValues = Array.isArray(value) ? value : [value];
  const values = relationValues.map((item) =>
    typeof item === "object" && item !== null && "uuid" in item
      ? item.uuid
      : item,
  );
  return property.constraint.settings?.multiple ? values : values[0];
}

watch(
  template,
  (newTemplate) => {
    if (!newTemplate) return;

    const values = { ...props.propertyValues };
    for (const property of newTemplate.properties ?? []) {
      if (!property.customId || values[property.customId] !== undefined) {
        continue;
      }
      values[property.customId] =
        property.propertyValue?.value ??
        props.defaultPropertyValues[property.customId] ??
        null;
      if (
        property.customId === ticketProps.requester &&
        values[property.customId] === null &&
        authStore.userId
      ) {
        values[property.customId] = [authStore.userId];
      }
    }
    emit("update:propertyValues", values);
  },
  { immediate: true },
);

watch(
  () => machineRestriction.value?.propertyDefaults,
  (propertyDefaults) => {
    if (!propertyDefaults) return;
    updatePropertyValues(
      Object.fromEntries(
        propertyDefaults
          .filter((restriction) => restriction.value !== undefined)
          .map((restriction) => [restriction.customId, restriction.value]),
      ),
    );
  },
  { immediate: true },
);

const missingRequiredFields = computed(() => {
  const missingFields = [];
  if (name.value.trim() === "") missingFields.push(t("title"));

  for (const property of createFormProperties.value) {
    if (!property.customId || !isPropertyVisible(property)) continue;
    const value = propertyValues.value[property.customId];
    if (
      property.constraint?.settings?.mandatory &&
      (value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0))
    ) {
      missingFields.push(property.name);
    }
  }

  missingFields.push(...props.additionalMissingRequiredFields());

  return missingFields;
});

async function createTicket() {
  await create(props.createStatus);
}

async function createDraft() {
  await create(props.draftStatus);
}

async function create(status: string) {
  if (!template.value) return;

  if (missingRequiredFields.value.length > 0) {
    ElNotification({
      title: t("error"),
      message: `${t("followingFieldsAreRequired")} ${missingRequiredFields.value.join(", ")}`,
      type: "error",
    });
    return;
  }

  creating.value = true;
  const values = {
    ...propertyValues.value,
    ...((template.value.properties ?? []).some(
      (property) => property.customId === ticketProps.status,
    )
      ? { [ticketProps.status]: status }
      : {}),
  };
  const templateProperties = (template.value.properties ?? [])
    .filter(
      (property) =>
        property.customId !== undefined &&
        values[property.customId] !== undefined,
    )
    .map((property) => ({
      property: property.customId as string,
      value: getPropertyValueForSaving(
        property,
        values[property.customId as string],
      ),
    }));
  const additionalProperties = Object.entries(props.additionalProperties)
    .filter(
      ([customId]) =>
        !(template.value?.properties ?? []).some(
          (property) => property.customId === customId,
        ),
    )
    .map(([property, value]) => ({ property, value }));

  try {
    const response = await createTwinWithProperties({
      name: name.value,
      description: description.value,
      namespace: template.value.namespace,
      language: template.value.language ?? applicationLanguage.value,
      type: template.value.type,
      initial: null,
      version: 0,
      active: true,
      locked: false,
      isTemplate: false,
      templateUuid: template.value.uuid,
      properties: [...templateProperties, ...additionalProperties],
      comment: "",
      longComment: "",
      ...props.additionalRequest,
    });
    emit("created", response.data.uuid);
  } catch (error) {
    showResponseErrorToUser(error, t);
  } finally {
    creating.value = false;
  }
}

onMounted(() => {
  void twinStore.getTwinTemplates();
  if (route.query.hwr) void setQueryMachineRestriction();
});

async function setQueryMachineRestriction() {
  queryMachineRestrictionLoading.value = true;
  try {
    const response = await searchMachineRestrictions({
      uuids: [route.query.hwr as string],
      page: 1,
      pageSize: 1,
    });
    if (response.data.result.length === 0) {
      ElNotification({ type: "error", message: t("qrCodeOutdated") });
    } else {
      queryMachineRestriction.value = response.data.result[0] ?? null;
    }
  } catch (error) {
    showResponseErrorToUser(error, t);
    throw error;
  } finally {
    queryMachineRestrictionLoading.value = false;
  }
}
</script>
